import React, { useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { v4 as uuidv4 } from 'uuid';
import { getPassCode, checkApiError } from '../../utils';
import { useNavigate } from "react-router-dom";

const Agenda = forwardRef(({ event, isAddEvent, setIsDateUpdated, isDateUpdated, showLoading }, ref) => {
  const navigate = useNavigate();
  const today = new Date().toISOString();
  const [eventStartDate] = useState(event?.eventStartDate || today);
  const [eventEndDate] = useState(event?.eventEndDate || today);
  const [timeZone] = useState(event?.eventTimeZone || "Pacific Standard Time");

  const getFullDate = (_date) =>{
    const date = new Date(_date);

    const d = (date.getDate() < 10) ? ('0' + date.getDate()) : date.getDate();
    const m = (date.getMonth() < 9) ? ('0' + (date.getMonth() + 1)) : (date.getMonth() + 1);
    const y = date.getFullYear();

    return y + '-' + m + '-' + d;
  }

  const getTimeZone = (_value) =>{
    const timeZones = [
      { name: "Pacific Time", value: "Pacific Standard Time" },
      { name: "Eastern Time", value: "Eastern Standard Time" },
      //{ name: "Indian Time (IT)", value: "India Standard Time" },
    ];

    const tz = timeZones.filter(item => item.value === _value)

    return tz.length > 0 ? `(${tz[0].name})` : `(${timeZone})`;
  } 

  const getDaysBetweenDates = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Crucial change:  Use .getTime() and integer division
    const diffInDays = Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    return diffInDays;
  }, []);

  const [excludedDays, setExcludedDays] = useState({});
  const daysBetweenDates = useMemo(() => getDaysBetweenDates(eventStartDate, eventEndDate), [getDaysBetweenDates, eventStartDate, eventEndDate]);

  const generateInitialAgenda = useCallback((startDate, endDate) => {
    const initialAgendaItems = [];
    const days = getDaysBetweenDates(startDate, endDate);

    for (let i = 0; i <= days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      //const dateString = date.toISOString().split("T")[0];
      const dateString = getFullDate(date);

      initialAgendaItems.push({ eventAgendaId: uuidv4(), eventDate: dateString, startTime: "09:00", endTime: "10:00", agendaDetails: '', teamsMeetingLink: "", isHighlighted: false, isExcluded: false });
    }
    return initialAgendaItems;
  }, [getDaysBetweenDates]);

  /* const updateAgendaDates = useCallback((startDate) => {
    const storedAgenda = localStorage.getItem(`agendaItems_${event.eventId}`);
    if (storedAgenda) {
      try {
        const agendaItems = JSON.parse(storedAgenda);
        const originalStartDate = new Date(agendaItems[0].eventDate);

        const updatedAgendaItems = agendaItems.map(item => {
          const originalDate = new Date(item.eventDate);
          const diffInDays = Math.round((originalDate - originalStartDate) / (1000 * 60 * 60 * 24));
          const newDate = new Date(startDate);
          newDate.setDate(newDate.getDate() + diffInDays);
          return { ...item, eventDate: newDate };
        });

        setAgendaItems(updatedAgendaItems);
      } catch (error) {
        console.error("Error parsing/updating agenda data:", error);
      }
    }

  }, [event.eventId]); */

  const [agendaItems, setAgendaItems] = useState(() => {
    // const storedAgenda = localStorage.getItem(`agendaItems_${event.eventId}`);
    /* const storedAgenda = [];
    if (storedAgenda && event.eventId) {
      try {
        return JSON.parse(storedAgenda); // Just return the parsed data
      } catch (error) {
        console.error("Error parsing agenda data:", error);
        return !event?.agenda || isAddEvent ? generateInitialAgenda(eventStartDate, eventEndDate) : event.agenda;
      }
    } else {
      return !event?.agenda || isAddEvent ? generateInitialAgenda(eventStartDate, eventEndDate) : event.agenda;
    } */
    return generateInitialAgenda(eventStartDate, eventEndDate);
  });

  const sortAgendaItemsByTime = (items) => {
    return items.sort((a, b) => {
      const timeA = new Date(`2000-01-01T${a.startTime}`); // Dummy date for time comparison
      const timeB = new Date(`2000-01-01T${b.startTime}`);
      return timeA - timeB;
    });
  };

  const [inputErrors, setInputErrors] = useState("");

  const validateInput = () => {
    const errors = [];
    let isValid = true;
    setInputErrors("")
    agendaItems.forEach((item, index) => {
      const itemErrors = {};
      if (!isValid) {
        return true;
      }
      if (!item.teamsMeetingLink) {
        itemErrors.teamsMeetingLink = "Meeting link is required."; // More specific error key
        setInputErrors("Meeting link is required.");
        isValid = false;
      } else if (item.teamsMeetingLink) {
        isValid = /(^https:\/\/?teams\.microsoft\.com\/[a-zA-Z0-9-])/.test(item.teamsMeetingLink);
        //(/^[a-zA-Z0-9\s-,.'&/]*$/.test(item.teamsMeetingLink))
        if (!isValid) {
          setInputErrors("Meeting link is invalid.");
        }
      }else{
        item.teamsMeetingLink = ""
      }

      const startTime = new Date(`2000-01-01T${item.startTime}`);
      const endTime = new Date(`2000-01-01T${item.endTime}`);

      if (isNaN(startTime) || isNaN(endTime)) {
        itemErrors.time = "Invalid time format."; // More specific error key
        isValid = false;
      } else if (endTime <= startTime) {
        itemErrors.time = "End time must be greater than start time."; // More specific error key
        setInputErrors("End time must be greater than start time.");
        isValid = false;
      }

      if (item.agendaDetails.length === 0 && !item.isExcluded) {
        setInputErrors("Please mention the agenda description.");
        isValid = false;
      }
      if (isValid) {
        isValid = (/^[a-zA-Z0-9\s-,.'&/()!:]*$/.test(item.agendaDetails));
        if (!isValid) {
          setInputErrors("Special characters not allowed except (. , ' - & / ! :).");
        }
      }

      if (Object.keys(itemErrors).length > 0) {
        errors[index] = itemErrors; // Store errors by index for easier display
      }
      console.log("input errro " + inputErrors)
    });
    //setInputErrors(errors);
    return isValid;
  };
  /* useEffect(() => {
    if (isAddEvent) {
      setAgendaItems(generateInitialAgenda(eventStartDate, eventEndDate));
    }
  }, [isAddEvent, eventStartDate, eventEndDate, generateInitialAgenda]); */ // Removed generateInitialAgenda

  useEffect(() => {
    const fetchAgenda = async () => {
      if (event.eventId && !isDateUpdated) {
        try {
          showLoading(true);
          const response = await fetch(`${process.env.REACT_APP_API_URL}/EventAgenda/getagendasByEventId?EventId=${event.eventId}`, {
            method: 'GET',
            headers: {
              "Authorization": `Bearer ${getPassCode()}`,
            }
          });

          if(checkApiError(response.status)){ navigate("/login"); return; }

          if (response.ok) {
            const data = await response.json();

            if (data.eventAgendaList && data.eventAgendaList.length > 0) {

              const agendaWithDateObjects = data.eventAgendaList.map(item => ({
                ...item,
                eventDate: item.eventDate.split("T")[0], // Convert date string to Date object
                startTime: item.startTime.split("T")[1].slice(0, 5),  // Convert time to a Date object (using a dummy date)
                endTime: item.endTime.split("T")[1].slice(0, 5),
                isExcluded: item.teamsMeetingLink ? false : false
              }));
              setAgendaItems(agendaWithDateObjects);
            } else {
              // If API returns empty, use the default generated agenda
              setAgendaItems(generateInitialAgenda(eventStartDate, eventEndDate));
            }

          } else {
            console.error('Error fetching agenda:', response.status);
            setAgendaItems(generateInitialAgenda(eventStartDate, eventEndDate));
          }

        } catch (error) {
          console.error("Error fetching agenda:", error);
          // If any error occurs, set the default agenda

          setAgendaItems(generateInitialAgenda(eventStartDate, eventEndDate));

        } finally {
          showLoading(false);
        }
      } else if (isAddEvent || isDateUpdated) { //Condition for add event
        setIsDateUpdated(false)
        setAgendaItems(generateInitialAgenda(eventStartDate, eventEndDate));
        event.eventId && sendToInnerDB(undefined, true);
      }
    };

    fetchAgenda();
  }, [event.eventId, isAddEvent, showLoading, generateInitialAgenda, eventStartDate, eventEndDate]);

  const handleExcludeDayChange = (dateString, bool) => {
    /* setExcludedDays(prevState => ({
      ...prevState,
      [dateString]: !prevState[dateString] // Toggle the excluded state
    })); */
    
    const checkData = agendaItems.filter(item => item.eventDate === dateString);

    if(checkData.length === 0){
      handleAddAgenda(dateString);
    }else{
      setAgendaItems(prevItems => {
        return prevItems.map(item => {
          if (item.eventDate === dateString) {
            return { ...item, isExcluded: bool }; // Add/update teamsMeetingLink
          }
          return item;
        });
      });
    }
  };
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('EN-US', options);
  }, []);


  const formatTime = useCallback((timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    return date.toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' });
  }, []);
  const handleteamsMeetingLinkChange = (date, link) => {
    setAgendaItems(prevItems => {
      return prevItems.map(item => {
        if (item.eventDate === date) {
          return { ...item, teamsMeetingLink: link }; // Add/update teamsMeetingLink
        }
        return item;
      });
    });
  };

  const getteamsMeetingLink = (date) => {
    const item = agendaItems.find(item => item.eventDate === date);
    return item ? item.teamsMeetingLink : '';
  }

  const handleAddAgenda = (dateString) => {
    setAgendaItems(prevItems => {
      const newDate = new Date(dateString);
      newDate.setHours(9, 0, 0, 0); // Default start time if no previous items
      //const newDateString = newDate.toISOString().split('T')[0];

      const newDateString = getFullDate(newDate);

      const existingLink = prevItems.find(item => item.eventDate === newDateString)?.teamsMeetingLink;

      // Find the last end time for the given date
      const lastEndTimeItem = prevItems
        .filter(item => item.eventDate === newDateString)
        .sort((a, b) => {
          const aEndTime = new Date(`${newDateString}T${a.endTime}`);
          const bEndTime = new Date(`${newDateString}T${b.endTime}`);
          return aEndTime - bEndTime; // Sort in ascending order of end time
        })
        .pop(); // Get the last item (with the latest end time)

      let startTime = "09:00"; // Default if no previous items
      if (lastEndTimeItem) {
        startTime = lastEndTimeItem.endTime;
        // Increment startTime if needed (e.g., add 15 minutes)
        let [hours, minutes] = startTime.split(':');
        minutes = parseInt(minutes, 10);
        //minutes += 15; // Example: Add 15 minutes
        if (minutes >= 60) {
          minutes -= 60;
          hours = parseInt(hours, 10) + 1;
          if (hours >= 24) {
            hours = 0; // Or handle overflow as needed
          }
        }
        startTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }


      const endTime = incrementTime(startTime); //Calculate end time based on start time


      const newItems = [...prevItems, {
        eventAgendaId: uuidv4(),
        eventDate: newDateString,
        startTime: startTime,
        endTime: endTime,
        agendaDetails: "",
        isHighlighted: false,
        isExcluded: false,
        teamsMeetingLink: existingLink || ""
      }];

      return newItems;
    });
  };

  const incrementTime = (time) => {
    let [hours, minutes] = time.split(':');
    minutes = parseInt(minutes, 10);
    minutes += 60; // Example: Add 60 minutes (1 hour)
    if (minutes >= 60) {
      minutes -= 60;
      hours = parseInt(hours, 10) + 1;
      if (hours >= 24) {
        hours = 0; // Or handle overflow as needed
      }
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleAgendaChange = (id, field, value) => {
    setAgendaItems(prevItems => prevItems.map(item =>
      item.eventAgendaId === id ? { ...item, [field]: value } : item  // Create a new object if the ID matches
    ));
  };

  const handleColorChange = (id) => {
    setAgendaItems(prevItems => prevItems.map(item =>
      item.eventAgendaId === id ? { ...item, isHighlighted: item.isHighlighted === true ? false : true } : item
    ));
  }

  const handleDeleteAgenda = (id) => {
    setAgendaItems(prevItems => {
      const newAgendaItems = prevItems.filter(item => item.eventAgendaId !== id);
      if (newAgendaItems.length === 0) {
        //setEventEndDate(eventStartDate); // reset end date to start date when all agenda items are removed.
      } else {
        let newEndDate = eventStartDate;
        for (const item of newAgendaItems) {
          if (item.eventDate > newEndDate) {
            //newEndDate = item.eventDate;
          }
        }
        //setEventEndDate(newEndDate);
      }
      return newAgendaItems; // Return the updated list of agenda items.
    });
  };


  useImperativeHandle(ref, () => ({
    sendToDB: (callback) => {
      return sendToInnerDB(callback);
    }
  }));
  const sendToInnerDB = async (callback, valiationSkip = false) => {
    try {
      showLoading(true);
      if (!valiationSkip && !validateInput()) {
        showLoading(false);
        callback && callback(false);
        return;
      }
      // Prepare the data for the API
      // Filter out any agenda items with undefined dates
      const validAgendaItems = agendaItems.filter(item => item.eventDate !== undefined);

      // If there are no valid agenda items, log a message or handle as appropriate.  You might want to return early or show a message to the user.
      if (validAgendaItems.length === 0) {
        setInputErrors('At least one valid agenda details is required.');
        console.warn("No agenda items with valid dates found.");
        // Consider handling this case, maybe returning or notifying the user
        showLoading(false);  // Don't forget this!
        callback && callback(false); // Call callback even if there are no valid items
        return; // Exit the function early
      }

      const eventAgendas = validAgendaItems.map(item => ({
        eventId: event.eventId,
        // eventDate: item.eventDate + "T00:00:00.101Z",
        eventDate: item.eventDate.includes('T') ? item.eventDate : `${item.eventDate}T00:00:00.101Z`,
        teamsMeetingLink: item.teamsMeetingLink,
        // startTime: `${item.eventDate}T${item.startTime}:00.101Z`,
        startTime: item.startTime.includes('T') ? item.startTime : `${item.eventDate}T${item.startTime}:00.101Z`,
        endTime: item.endTime.includes('T') ? item.endTime : `${item.eventDate}T${item.endTime}:00.101Z`,
        agendaDetails: item.agendaDetails,
        isHighlighted: item.isHighlighted === true,
      }));


      const response = await fetch(`${process.env.REACT_APP_API_URL}/EventAgenda/insertupdateEventAgendas`, {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${getPassCode()}`, // Include authorization header
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ eventAgendas }) // Correct JSON structure
      });

      if(checkApiError(response.status)){ navigate("/login"); return; }

      if (response.ok) {
        //const data = await response.json();
        // console.log("Agenda saved successfully:"); // Log success response
        //Consider updating the agendaItems state with the returned data from the API after a successful save if your API sends back the updated agenda items.
        callback && callback(true); // Indicate success
      } else {
        console.error('Error saving agenda:', response.status, await response.text()); //Log the error and response text for debugging.
        // Handle errors, perhaps show a user-friendly message
        callback && callback(false); // Indicate failure
      }
    } catch (error) {
      console.error("Error saving agenda:", error);
      // Handle errors, perhaps show a user-friendly message
      callback && callback(false); // Indicate failure
    } finally {
      showLoading(false);
    }
  };

  const checkIsExcluded = (_data)=>{
    return false;
    /* if(_data.length > 0){
      return _data[0]?.isExcluded
    }else{
      return true;
    } */
  }

  return (
    <div className="agenda-container">
      <p className="roboto-bold fw-500 mb-4 color-gray t-h">Add Agenda</p>


      {Array.from({ length: daysBetweenDates + 1 }).map((_, dayIndex) => {
        const currentDate = new Date(eventStartDate);
        currentDate.setDate(currentDate.getDate() + dayIndex);
        //const dateString = currentDate.toISOString().split('T')[0];

        const dateString = getFullDate(currentDate);

        const itemsForCurrentDate = agendaItems.filter(item => item.eventDate === dateString);
        // Sort the items for the current date:
        const sortedItemsForCurrentDate = sortAgendaItemsByTime(itemsForCurrentDate);

        return (
          <div className={`card day-agenda mb-4 ${checkIsExcluded(sortedItemsForCurrentDate) || excludedDays[dateString] ? 'fade-out' : 'fade-in'}`} key={dayIndex} style={{ transition: 'opacity 0.3s ease' }}>
            <div className="card-body pt-0">
              <div className="agenda-list bg-purple rounded">
                <div className="agenda-list-title fw-500">
                  <span className="sTtlDate">
                    {/* Calculate and display the date for each card */}
                    {formatDate(new Date(eventStartDate).setDate(new Date(eventStartDate).getDate() + dayIndex))}
                  </span>
                  <span className="me-2 ms-2">|</span>
                  {/*Conditinally render the time element */}
                  {sortedItemsForCurrentDate.length > 0 && (
                    <span className="sTime me-1">
                      {formatTime(sortedItemsForCurrentDate[0].startTime)} - {sortedItemsForCurrentDate[sortedItemsForCurrentDate.length - 1].endTime && formatTime(sortedItemsForCurrentDate[sortedItemsForCurrentDate.length - 1].endTime)}
                    </span>
                  )}
                  <span className="sTimezone">{getTimeZone(timeZone) || ''}</span>
                </div>
                <div className="meeting-link input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 48 48">
                      <path fill="#5059c9" d="M44,22v8c0,3.314-2.686,6-6,6s-6-2.686-6-6V20h10C43.105,20,44,20.895,44,22z M38,16	c2.209,0,4-1.791,4-4c0-2.209-1.791-4-4-4s-4,1.791-4,4C34,14.209,35.791,16,38,16z"></path><path fill="#7b83eb" d="M35,22v11c0,5.743-4.841,10.356-10.666,9.978C19.019,42.634,15,37.983,15,32.657V20h18	C34.105,20,35,20.895,35,22z M25,17c3.314,0,6-2.686,6-6s-2.686-6-6-6s-6,2.686-6,6S21.686,17,25,17z"></path><circle cx="25" cy="11" r="6" fill="#7b83eb"></circle><path d="M26,33.319V20H15v12.657c0,1.534,0.343,3.008,0.944,4.343h6.374C24.352,37,26,35.352,26,33.319z" opacity=".05"></path><path d="M15,20v12.657c0,1.16,0.201,2.284,0.554,3.343h6.658c1.724,0,3.121-1.397,3.121-3.121V20H15z" opacity=".07"></path><path d="M24.667,20H15v12.657c0,0.802,0.101,1.584,0.274,2.343h6.832c1.414,0,2.56-1.146,2.56-2.56V20z" opacity=".09"></path><linearGradient id="DqqEodsTc8fO7iIkpib~Na_zQ92KI7XjZgR_gr1" x1="4.648" x2="23.403" y1="14.648" y2="33.403" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#5961c3"></stop><stop offset="1" stopColor="#3a41ac"></stop></linearGradient><path fill="url(#DqqEodsTc8fO7iIkpib~Na_zQ92KI7XjZgR_gr1)" d="M22,34H6c-1.105,0-2-0.895-2-2V16c0-1.105,0.895-2,2-2h16c1.105,0,2,0.895,2,2v16	C24,33.105,23.105,34,22,34z"></path><path fill="#fff" d="M18.068,18.999H9.932v1.72h3.047v8.28h2.042v-8.28h3.047V18.999z"></path>
                    </svg>
                  </span>
                  <input type="text" name="teamsMeetingLink" className="form-control" placeholder="Enter meeting link here" aria-label="Meeting link" aria-describedby="basic-addon1" value={getteamsMeetingLink(dateString)} onChange={(e) => handleteamsMeetingLinkChange(dateString, e.target.value)} // Update state
                  disabled={excludedDays[dateString] || checkIsExcluded(sortedItemsForCurrentDate) ? true : false}
                  />
                </div>
                <div className="agenda-exclude-check hide d-none">
                  <input type="checkbox" className="icons icon-check" onChange={(e) => handleExcludeDayChange(dateString, e.target.checked)}
                    checked={checkIsExcluded(sortedItemsForCurrentDate) || excludedDays[dateString] || false} />
                  <label className="form-check-label ms-1">Exclude</label>
                </div>
              </div>

              <div className="mt-4">
                {sortedItemsForCurrentDate.map((item, index) => (
                  <div className={`agenda-list-items row align-items-center mb-3 ${item.isHighlighted ? 'color-blue' : ''}  ${excludedDays[dateString] || item.isExcluded ? 'pe-none' : ''}`} key={index}>
                    <div className="col-6 col-md-2 mb-2">
                      <input
                        type="time"
                        className="form-control"
                        value={item.startTime}
                        onChange={(e) =>
                          handleAgendaChange(item.eventAgendaId, "startTime", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-6 col-md-2 mb-2">
                      <input
                        type="time"
                        className="form-control"
                        value={item.endTime}
                        onChange={(e) => handleAgendaChange(item.eventAgendaId, "endTime", e.target.value)}
                      />
                    </div>
                    <div className="col-md-7 mb-1">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter agenda here"
                        value={item.agendaDetails}
                        onChange={(e) => handleAgendaChange(item.eventAgendaId, "agendaDetails", e.target.value)} />
                    </div>
                    <div className="col-md-1 d-flex justify-content-start justify-content-md-center mb-0 mb-sm-1">
                      <button type="button" className="btn btn-sm icon-trash light-gray me-1" onClick={() => handleDeleteAgenda(item.eventAgendaId)} >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                          <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                        </svg>
                      </button>

                      <button title="Highlight" type="button" className={`btn btn-sm icon-highlighter light-gray outline-none ${item.isHighlighted ? 'active' : ''}`} onClick={(e) => { handleColorChange(item.eventAgendaId) }} >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-highlighter" viewBox="0 0 16 16">
                          <path d="M11.096.644a2 2 0 0 1 2.791.036l1.433 1.433a2 2 0 0 1 .035 2.791l-.413.435-8.07 8.995a.5.5 0 0 1-.372.166h-3a.5.5 0 0 1-.234-.058l-.412.412A.5.5 0 0 1 2.5 15h-2a.5.5 0 0 1-.354-.854l1.412-1.412A.5.5 0 0 1 1.5 12.5v-3a.5.5 0 0 1 .166-.372l8.995-8.07zm-.115 1.47L2.727 9.52l3.753 3.753 7.406-8.254zm3.585 2.17.064-.068a1 1 0 0 0-.017-1.396L13.18 1.387a1 1 0 0 0-1.396-.018l-.068.065zM5.293 13.5 2.5 10.707v1.586L3.707 13.5z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" className={`btn btn-purple btn-sm btn-add-ageda-item mt-2 ${excludedDays[dateString] || checkIsExcluded(sortedItemsForCurrentDate) ? 'pe-none' : ''}`} onClick={() => handleAddAgenda(dateString)} > Add Agenda Item </button>
            </div>
          </div>
        );
      })}
      {inputErrors.length > 0 && <div className="text-danger small mt-1">{inputErrors}</div>}
    </div>
  );
});

export default Agenda;
